import { useEffect, useMemo, useState } from 'react';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box, Flex, Link, Tag, useDisclosure } from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useRetailers } from 'core/redux/selectors/dataSelectors';
import { SHOPS_CLUSTER_LIST_FIELDS } from 'pages/ClusterManagement/const';
import { useLazyGetShopClusterQuery } from 'core/API/clusterApi';
import { TCluster, TShop } from 'pages/ClusterManagement/types';

const EMPTY_DATA: any = [];

const useShopClusterContainer = () => {
    const translate = useTranslation();
    const [getShopClusterQuery, { data: shopClusterData, isFetching: isLoading }] = useLazyGetShopClusterQuery();
    const { retailersById } = useRetailers();

    const [actionModalType, setActionModalType] = useState<'edit' | 'delete' | undefined>();
    const [selectedRetailers, setSelectedRetailers] = useState<(string | number)[]>(EMPTY_DATA);
    const [currentShop, setCurrentShop] = useState<TShop | null>(null);

    const actionModalProps = useDisclosure();

    const handleOpenEditAddModal = (shop: TShop) => {
        setActionModalType('edit');
        setCurrentShop(shop);
        actionModalProps.onOpen();
    };

    useEffect(() => {
        selectedRetailers.length && getShopClusterQuery({ retailerIds: selectedRetailers });
    }, [selectedRetailers]);

    const columns: ColumnDef<TShop, any>[] = useMemo(() => {
        const filteredTableHeader: ColumnDef<TShop, any>[] = SHOPS_CLUSTER_LIST_FIELDS.map(
            (header: string): ColumnDef<TShop, any> => {
                switch (header) {
                    case 'retailer':
                        return {
                            header: () => translate(`shopCluster.column.${header}`) as string,
                            id: header,
                            accessorFn: (row) => retailersById[row.retailer_id]?.name || '',
                            cell: (info) => info.getValue(),
                        } as ColumnDef<TShop, any>;
                    case 'shop_id':
                        return {
                            header: () => translate(`shopCluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return (
                                    <Flex>
                                        <Link onClick={() => handleOpenEditAddModal(original)} color="blue.500">
                                            {original[header as keyof TShop] as string}
                                        </Link>
                                    </Flex>
                                );
                            },
                        };
                    case 'cluster':
                        return {
                            header: () => translate(`shopCluster.column.${header}`) as string,
                            id: header,
                            accessorFn: (row) => row.clusters?.map((cluster: TCluster) => cluster.label).join(', '),
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return (
                                    <Flex gap={2} maxW="200px" flexWrap="wrap">
                                        {original.clusters.map((cluster: TCluster) => (
                                            <Flex>
                                                <Box
                                                    w="20px"
                                                    h="20px"
                                                    borderRadius="50%"
                                                    bgColor={cluster.color}
                                                    border={cluster.color === '#ffffff' ? '1px solid #E2E8F0' : 'none'}
                                                    mr="4px"
                                                    display="inline-block"
                                                />
                                                {cluster.label}
                                            </Flex>
                                        ))}
                                    </Flex>
                                );
                            },
                        };
                    default:
                        return {
                            header: () => translate(`shopCluster.column.${header}`) as string,
                            accessorKey: header,
                            meta: {
                                minWidth: '200px',
                                width: '200px',
                            },
                            cell: (info) => {
                                const { original } = info.row;
                                return <Flex>{original[header as keyof TShop] as string}</Flex>;
                            },
                        };
                }
            },
        );

        return filteredTableHeader;
    }, [retailersById]);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const table = useReactTable<TShop>({
        data: (selectedRetailers.length && shopClusterData?.data) || EMPTY_DATA,
        columns,
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            isLoading: isLoading,
            table,
            actionModalProps,
            selectedRetailers,
            actionModalType,
            currentShop,
            setCurrentShop,
            setActionModalType,
            setSelectedRetailers,
        }),
        [isLoading, table, actionModalProps.isOpen, actionModalType, currentShop],
    );

    return props;
};

export type TShopClusterViewer = ReturnType<typeof useShopClusterContainer>;
export default useShopClusterContainer;
